import {groupBy1Constants} from "../constants/groupBy1Constants";
import {LEVEL_1, LEVEL_2, LEVEL_3} from "../actions/groupBy3Action";

const initialState = {
    data: {
        records: [],
        page: 0,
        recordsPerPage: 0,
        totalRecords: 0,
        total: {}
    },
    integration: null,
    trafficSource: null,
    errorMsg: "",
    isLoading: false,
    name: "",
    orderBy: "",
    sortBy: "",
    sortColumns: [],
    rerender: false,
    selectedUUIDs: new Set(),
    selectedRows: new Set(),
    groupBy: {
        "label": "Country",
        "groupBy": "country",
        "sortBy": "country"
    }
};

function isDescendant(item, clickedRowUUID, recordMap) {
    let parentId = item.parentRowUUID;

    while (parentId != null) {
        if (parentId === clickedRowUUID) {
            return true;
        }

        const parent = recordMap.get(parentId);
        if (!parent) return false; // broken chain → stop safely

        parentId = parent.parentRowUUID;
    }

    return false;
}

const groupBy1Reducer = (state = initialState, action) => {
    switch (action.type) {
        case groupBy1Constants.GROUP_BY_1_LOADING_SPINNER:
            return {
                ...state,
                isLoading: true,
            };
        case groupBy1Constants.GROUP_BY_1_STOP_LOADING_SPINNER:
            return {
                ...state,
                isLoading: false,
            };
        case groupBy1Constants.GROUP_BY_1_INFINITY_SCROLL:
            return {
                ...state,
                data: {
                    records: [...state.data.records, ...action?.result?.records],
                    page: action?.result?.page,
                    recordsPerPage: action?.result?.recordsPerPage,
                    totalRecords: action?.result?.totalRecords,
                    total: action?.result?.total
                },
                errorMsg: '',
                isLoading: false,
            };
        case groupBy1Constants.GROUP_BY_1_GET_DATA:
            return {
                ...state,
                data: {
                    records: action?.result?.records,
                    page: action?.result?.page,
                    recordsPerPage: action?.result?.recordsPerPage,
                    totalRecords: action?.result?.totalRecords,
                    total: action?.result?.total
                },
                errorMsg: '',
                isLoading: false,
                rerender: false,
            };
        case groupBy1Constants.GROUP_BY_1_ERROR:
            return {
                ...state,
                errorMsg: 'Unable to load data. Please try again.',
                rerender: false,
                isLoading: false,
            };
        case groupBy1Constants.GROUP_BY_1_SET_TRAFFIC_SOURCE_DATA:
            return {
                ...state,
                trafficSource: action.trafficSource,
            };
        case groupBy1Constants.GROUP_BY_1_SET_INTEGRATION_DATA:
            return {
                ...state,
                integration: action.integration,
            };
        case groupBy1Constants.GROUP_BY_1_SET_SELECTED_NAME:
            return {
                ...state,
                name: action.name
            };
        case groupBy1Constants.GROUP_BY_1_UPDATE_ORDER_BY:
            return {
                ...state,
                orderBy: action.orderBy
            };
        case groupBy1Constants.GROUP_BY_1_UPDATE_SORT_BY:
            return {
                ...state,
                sortBy: action.sortBy
            };
        case groupBy1Constants.GROUP_BY_1_UPDATE_SORT_COLUMNS:
            return {
                ...state,
                sortColumns: action.sortColumns
            };
        case groupBy1Constants.GROUP_BY_1_RERENDER:
            return {
                ...state,
                rerender: true
            };
        case groupBy1Constants.GROUP_BY_1_SET_SELECTED_ROW:
            return {
                ...state,
                selectedUUIDs: action?.payload?.selectedUUIDs,
                selectedRows: action?.payload?.selectedRows
            };
        case groupBy1Constants.GROUP_BY_1_UPDATE_GROUP_BY:
            return {
                ...state,
                groupBy: action.groupBy
            };
        case groupBy1Constants.GROUP_BY_1_EXPAND_ROW: {

            let expandStatus = action?.payload?.expandStatus;
            let clickedRowUUID = action?.payload?.clickedRowUUID;
            const children = Array.isArray(action?.payload?.records) ? action.payload.records : [];
            const oldRecords = Array.isArray(state?.data?.records) ? state.data.records : [];

            if (expandStatus) {
                const newRecords = [];
                const existingUUIDs = new Set(oldRecords.map(r => r.uuid));

                oldRecords.forEach(row => {
                    const cloned = {...row};
                    newRecords.push(cloned);

                    if (row.uuid === clickedRowUUID) {
                        cloned.expanded = true;

                        const toInsert = children
                            .filter(c => !existingUUIDs.has(c.uuid))
                            .map(c => ({
                                ...c,
                                parentRowUUID: clickedRowUUID
                            }));

                        if (toInsert.length) {
                            newRecords.push(...toInsert);
                        }
                    }
                });

                return {
                    ...state,
                    data: {
                        ...state.data,
                        records: newRecords
                    },
                    isLoading: false
                };
            } else {
                const recordMap = new Map();
                oldRecords.forEach(r => recordMap.set(r.uuid, r));

                const newRecords = [];

                oldRecords.forEach(row => {
                    // Remove ALL descendants (children, grandchildren, etc.)
                    if (isDescendant(row, clickedRowUUID, recordMap)) {
                        return;
                    }

                    const cloned = {...row};

                    // Collapse only the clicked row
                    if (row.uuid === clickedRowUUID) {
                        cloned.expanded = false;
                    }

                    newRecords.push(cloned);
                });

                return {
                    ...state,
                    data: {
                        ...state.data,
                        records: newRecords
                    },
                    isLoading: false
                };
            }
        }
        case groupBy1Constants.GROUP_BY_1_LOAD_MORE_BUTTON: {

            const clickedRowUUID = action?.payload?.clickedRowUUID;   // the load-more row to remove (preferred insertion point)
            const moreRows = Array.isArray(action?.payload?.records) ? action.payload.records : [];
            const newLoadMoreRow = action?.payload?.newLoadMoreRow || null; // optional new "load-more" placeholder to re-insert

            const oldRecords = Array.isArray(state?.data?.records) ? state.data.records : [];
            // find index of clickedRowUUID if present
            const clickedIdx = clickedRowUUID ? oldRecords.findIndex(r => r.uuid === clickedRowUUID) : -1;

            if (clickedIdx !== -1) {
                // Build newRecords by: everything before clickedIdx, then insert moreRows (+ optional new load-more row), then everything after clickedIdx (skipping clicked row)
                const before = oldRecords.slice(0, clickedIdx);
                const after = oldRecords.slice(clickedIdx + 1); // skip the clicked load-more row

                const toInsert = [...moreRows];
                if (newLoadMoreRow) toInsert.push(newLoadMoreRow);

                const newRecords = [...before, ...toInsert, ...after];

                return {
                    ...state,
                    data: {
                        ...state.data,
                        records: newRecords
                    },
                    isLoading: false
                };
            }

            // Fallback: clicked & partner not found -> just append to end (safe)
            const fallback = [...oldRecords.filter(r => r.uuid !== clickedRowUUID), ...moreRows];
            if (newLoadMoreRow) fallback.push(newLoadMoreRow);

            return {
                ...state,
                data: {
                    ...state.data,
                    records: fallback
                },
                isLoading: false
            };
        }
        case groupBy1Constants.GROUP_BY_1_UPDATE_TREE_VIEW_DATA: {
            const groupByNumber = action?.payload?.groupByNumber; // 2 or 3
            const oldRecords = Array.isArray(state?.data?.records) ? state.data.records : [];

            let records;

            if (groupByNumber === 2) {
                // ─────────────────────────────────────
                // GROUP BY 2
                // Remove LEVEL 2 & LEVEL 3
                // Collapse all LEVEL 1
                // ─────────────────────────────────────
                records = oldRecords
                    .filter(row => row?.type === LEVEL_1)
                    .map(row => ({
                        ...row,
                        expanded: false
                    }));

            } else if (groupByNumber === 3) {
                // ─────────────────────────────────────
                // GROUP BY 3
                // Remove LEVEL 3 only
                // Collapse all LEVEL 2
                // Leave LEVEL 1 untouched
                // ─────────────────────────────────────
                records = oldRecords
                    .filter(row => row?.type !== LEVEL_3)
                    .map(row => {
                        if (row?.type === LEVEL_2) {
                            return {...row, expanded: false};
                        }
                        return {...row};
                    });

            } else {
                // Safety fallback — do nothing
                records = oldRecords.map(row => ({...row}));
            }

            return {
                ...state,
                data: {
                    ...state.data,
                    records
                },
                isLoading: false
            };
        }
        case groupBy1Constants.GROUP_BY_1_RESET:
            return initialState;
        default:
            return state;
    }
};

export default groupBy1Reducer;